/**
 * Turning a Ticketmaster result into the fields the event form expects.
 */

// GlobalEvent caps description at 500; the form's own limit is lower still, and
// a wall of vendor boilerplate is not a description anyone wants to read.
const MAX_DESCRIPTION = 200

/**
 * A readable description for an imported event.
 *
 * This used to be `Event from Ticketmaster: <url>`, which put a bare tracking
 * link (`https://on.fgtix.com/trk/...`) in the description of every imported
 * event — no information, and it looks like spam in the passport.
 *
 * Preference order is most-specific-first: the event's own blurb, then its
 * door/age note, then a sentence built from what we do know. The URL is never
 * the description; it belongs in the ticket link.
 */
export const describeTicketmasterEvent = (event = {}) => {
  const candidate = [event.info, event.description, event.pleaseNote]
    .map(value => (typeof value === 'string' ? value.trim() : ''))
    .find(value => value.length > 0)

  if (candidate) {
    return candidate.length > MAX_DESCRIPTION
      ? `${candidate.slice(0, MAX_DESCRIPTION - 1).trimEnd()}…`
      : candidate
  }

  // Nothing usable from the API — describe it from the facts we have rather
  // than leaving the field empty, which fails the form's required check.
  const genre = event.genre || event.type
  const venue = event.venue?.name
  const city = event.venue?.city

  const parts = []
  if (genre) parts.push(genre)
  parts.push(genre ? 'event' : 'Live event')
  if (venue) parts.push(`at ${venue}`)
  else if (city) parts.push(`in ${city}`)

  const sentence = parts.join(' ')
  return sentence.charAt(0).toUpperCase() + sentence.slice(1)
}

export default describeTicketmasterEvent
